/**
 * REDCap / CDISC ODM historical clinical-data import (Phase 2).
 *
 * Streams the <ClinicalData> section of an ODM export in chunks, resolves each
 * SubjectKey to an already-imported patient profile by its deterministic
 * placeholder email, creates the matching patient_visits rows, and writes one
 * patient_form_submissions row per exported form instance.
 *
 * Contains PII — admin only, gated behind an explicit dry run before any write.
 * Every written row is stamped `submission_data._source` so migrated records
 * stay distinguishable from data captured in BRIDGE.
 */
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Database,
  Loader2,
  ShieldAlert,
  StopCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { parseOdmXml, type OdmImportResult } from "@/lib/ecrf/odmImporter";
import {
  readOdmMetadataHead,
  streamOdmSubjects,
  type OdmSubjectRecord,
} from "@/lib/ecrf/odmClinicalStream";
import {
  buildImportKey,
  buildOdmDecoder,
  completeFlagLabel,
  decodeFormInstance,
  statusFromCompleteFlag,
  type LegacySourceStamp,
  type OdmDecoder,
} from "@/lib/ecrf/odmClinicalDecode";
import { SERURUBELE_SITE_ID } from "@/utils/serurubeleRedcapTransformer";
import { logAuditTrail } from "@/utils/auditLogger";

const sb: any = supabase;

const DEFAULT_EMAIL_SUFFIX = "@serurubele.aurum.redcap.import";
const SUBMISSION_BATCH = 200;
const PAGE = 1000;

interface RunReport {
  mode: "dry_run" | "import";
  startedAt: string;
  finishedAt?: string;
  subjectsSeen: number;
  subjectsMatched: number;
  subjectsUnmatched: string[];
  formInstancesSeen: number;
  formInstancesUnknownForm: Record<string, number>;
  submissionsWritten: number;
  submissionsSkippedExisting: number;
  submissionsSkippedEmpty: number;
  visitsCreated: number;
  visitsReused: number;
  statusCounts: Record<string, number>;
  errors: string[];
  stopped: boolean;
}

function emptyReport(mode: RunReport["mode"]): RunReport {
  return {
    mode,
    startedAt: new Date().toISOString(),
    subjectsSeen: 0,
    subjectsMatched: 0,
    subjectsUnmatched: [],
    formInstancesSeen: 0,
    formInstancesUnknownForm: {},
    submissionsWritten: 0,
    submissionsSkippedExisting: 0,
    submissionsSkippedEmpty: 0,
    visitsCreated: 0,
    visitsReused: 0,
    statusCounts: {},
    errors: [],
    stopped: false,
  };
}

export default function RedcapOdmClinicalImport() {
  const [siteId, setSiteId] = useState<string>(SERURUBELE_SITE_ID);
  const [emailSuffix, setEmailSuffix] = useState<string>(DEFAULT_EMAIL_SUFFIX);
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<OdmImportResult | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [parsingMeta, setParsingMeta] = useState(false);
  const [running, setRunning] = useState<false | "dry_run" | "import">(false);
  const [progress, setProgress] = useState({ pct: 0, subjects: 0 });
  const [report, setReport] = useState<RunReport | null>(null);
  const [lastDryRunOk, setLastDryRunOk] = useState(false);
  const stopRef = useRef(false);

  const { data: sites } = useQuery({
    queryKey: ["odm-clinical-sites"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("trial_sites_safe")
        .select("id, name, country")
        .order("name");
      if (error) throw error;
      return (data || []) as { id: string; name: string; country: string | null }[];
    },
  });

  const { data: siteForms } = useQuery({
    queryKey: ["odm-clinical-site-forms", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await sb
        .from("trial_protocol_forms")
        .select("id, form_name, version, form_schema")
        .eq("trial_site_id", siteId);
      if (error) throw error;
      return (data || []) as {
        id: string;
        form_name: string;
        version: string | null;
        form_schema: any;
      }[];
    },
  });

  /** ODM FormOID / form name -> trial_protocol_forms.id for the chosen site. */
  const formIdByOid = useMemo(() => {
    const m = new Map<string, string>();
    (siteForms || []).forEach((f) => {
      const oid = f.form_schema?.odm_form_oid;
      if (oid) m.set(String(oid), f.id);
      m.set(f.form_name, f.id);
    });
    return m;
  }, [siteForms]);

  const decoder: OdmDecoder | null = useMemo(
    () => (meta ? buildOdmDecoder(meta) : null),
    [meta],
  );

  const formCoverage = useMemo(() => {
    if (!meta) return [];
    return meta.forms.map((f) => ({
      formName: f.formName,
      formOid: f.odm_form_oid,
      targetFormId: formIdByOid.get(f.odm_form_oid) || formIdByOid.get(f.formName) || null,
    }));
  }, [meta, formIdByOid]);

  const missingForms = formCoverage.filter((f) => !f.targetFormId);

  const handleFile = async (f: File) => {
    setFile(f);
    setMeta(null);
    setMetaError(null);
    setReport(null);
    setLastDryRunOk(false);
    setParsingMeta(true);
    try {
      const head = await readOdmMetadataHead(f);
      setMeta(parseOdmXml(head));
    } catch (err) {
      setMetaError(err instanceof Error ? err.message : "Could not read the ODM metadata section");
    } finally {
      setParsingMeta(false);
    }
  };

  /* ---------------- Lookups ---------------- */

  async function loadProfilesBySuffix(suffix: string) {
    const map = new Map<string, string>();
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await sb
        .from("profiles")
        .select("id, email")
        .like("email", `%${suffix}`)
        .range(from, from + PAGE - 1);
      if (error) throw error;
      (data || []).forEach((p: any) => {
        if (p.email) map.set(String(p.email).toLowerCase(), p.id);
      });
      if (!data || data.length < PAGE) break;
    }
    return map;
  }

  async function loadExistingVisits(patientIds: string[]) {
    const map = new Map<string, string>();
    for (let i = 0; i < patientIds.length; i += 200) {
      const chunk = patientIds.slice(i, i + 200);
      const { data, error } = await sb
        .from("patient_visits")
        .select("id, patient_id, visit_number")
        .in("patient_id", chunk);
      if (error) throw error;
      (data || []).forEach((v: any) => map.set(`${v.patient_id}|${v.visit_number}`, v.id));
    }
    return map;
  }

  async function loadExistingImportKeys(formIds: string[]) {
    const keys = new Set<string>();
    if (!formIds.length) return keys;
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await sb
        .from("patient_form_submissions")
        .select("import_key:submission_data->_source->>import_key")
        .in("form_id", formIds)
        .range(from, from + PAGE - 1);
      if (error) throw error;
      (data || []).forEach((r: any) => {
        if (r.import_key) keys.add(String(r.import_key));
      });
      if (!data || data.length < PAGE) break;
    }
    return keys;
  }

  /* ---------------- The run ---------------- */

  async function run(mode: "dry_run" | "import") {
    if (!file || !meta || !decoder) return;
    if (!siteId) {
      toast.error("Choose a destination trial site first");
      return;
    }
    if (mode === "import" && missingForms.length) {
      toast.error("Run the Phase 1 form import first", {
        description: `${missingForms.length} exported form(s) have no eCRF definition at this site.`,
      });
      return;
    }

    stopRef.current = false;
    setRunning(mode);
    setProgress({ pct: 0, subjects: 0 });
    const rep = emptyReport(mode);
    setReport({ ...rep });

    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("You must be signed in to run the migration.");

      const profileByEmail = await loadProfilesBySuffix(emailSuffix);
      if (profileByEmail.size === 0) {
        throw new Error(
          `No profiles found ending in "${emailSuffix}". Check the placeholder email suffix.`,
        );
      }

      const formIds = Array.from(new Set(formCoverage.map((f) => f.targetFormId).filter(Boolean) as string[]));
      const existingKeys = mode === "import" ? await loadExistingImportKeys(formIds) : new Set<string>();
      const visitCache =
        mode === "import"
          ? await loadExistingVisits(Array.from(new Set(profileByEmail.values())))
          : new Map<string, string>();

      const pendingSubmissions: any[] = [];

      const flush = async () => {
        if (!pendingSubmissions.length) return;
        const batch = pendingSubmissions.splice(0, pendingSubmissions.length);
        const { error } = await sb.from("patient_form_submissions").insert(batch);
        if (error) {
          rep.errors.push(`Batch of ${batch.length} submissions failed: ${error.message}`);
        } else {
          rep.submissionsWritten += batch.length;
        }
      };

      const handleSubject = async (subject: OdmSubjectRecord) => {
        rep.subjectsSeen++;
        const email = `${subject.subjectKey}${emailSuffix}`.toLowerCase();
        const patientId = profileByEmail.get(email);
        if (!patientId) {
          if (rep.subjectsUnmatched.length < 500) rep.subjectsUnmatched.push(subject.subjectKey);
          return;
        }
        rep.subjectsMatched++;

        for (const instance of subject.forms) {
          rep.formInstancesSeen++;
          const decoded = decodeFormInstance(decoder, instance);
          if (!decoded) {
            rep.formInstancesUnknownForm[instance.formOid] =
              (rep.formInstancesUnknownForm[instance.formOid] || 0) + 1;
            continue;
          }
          const formId =
            formIdByOid.get(instance.formOid) || formIdByOid.get(decoded.formName) || null;
          if (!formId) {
            rep.formInstancesUnknownForm[instance.formOid] =
              (rep.formInstancesUnknownForm[instance.formOid] || 0) + 1;
            continue;
          }
          if (decoded.answeredCount === 0 && decoded.completeFlag === null) {
            rep.submissionsSkippedEmpty++;
            continue;
          }

          const importKey = buildImportKey(subject.subjectKey, instance.formOid, instance.repeatKey);
          if (existingKeys.has(importKey)) {
            rep.submissionsSkippedExisting++;
            continue;
          }

          const status = statusFromCompleteFlag(decoded.completeFlag);
          rep.statusCounts[status] = (rep.statusCounts[status] || 0) + 1;

          // Visit resolution: submissions hang off a visit where the export gave us one.
          let visitId: string | null = null;
          if (decoded.visitNumber && decoded.visitDate) {
            const cacheKey = `${patientId}|${decoded.visitNumber}`;
            const cached = visitCache.get(cacheKey);
            if (cached) {
              visitId = cached;
              rep.visitsReused++;
            } else if (mode === "import") {
              const { data: v, error: vErr } = await sb
                .from("patient_visits")
                .insert({
                  patient_id: patientId,
                  trial_site_id: siteId,
                  visit_number: decoded.visitNumber,
                  visit_type: decoded.visitNumber === 1 ? "screening" : "unscheduled",
                  visit_status: "completed",
                  scheduled_date: decoded.visitDate,
                  actual_date: decoded.visitDate,
                  notes: "Migrated from REDCap ODM export",
                })
                .select("id")
                .maybeSingle();
              if (vErr) {
                rep.errors.push(
                  `Visit ${decoded.visitNumber} for subject ${subject.subjectKey}: ${vErr.message}`,
                );
              } else if (v?.id) {
                visitCache.set(cacheKey, v.id);
                visitId = v.id;
                rep.visitsCreated++;
              }
            } else {
              rep.visitsCreated++;
            }
          }

          const source: LegacySourceStamp = {
            system: "redcap_odm",
            project: meta.studyName || meta.studyOid,
            study_oid: meta.studyOid,
            export_timestamp: meta.sourceDateTime,
            original_form_oid: instance.formOid,
            original_form_name: decoded.formName,
            subject_key: subject.subjectKey,
            repeat_key: instance.repeatKey,
            redcap_complete: decoded.completeFlag,
            redcap_complete_label: completeFlagLabel(decoded.completeFlag),
            imported_from_file: file.name,
            imported_at: new Date().toISOString(),
            import_key: importKey,
            unmapped_item_oids: decoded.unmappedItemOids.length
              ? decoded.unmappedItemOids
              : undefined,
          };

          if (mode === "dry_run") {
            rep.submissionsWritten++;
            continue;
          }

          existingKeys.add(importKey);
          pendingSubmissions.push({
            patient_id: patientId,
            form_id: formId,
            visit_id: visitId,
            status,
            submitted_by: userId,
            submitted_at: decoded.visitDate
              ? new Date(`${decoded.visitDate}T00:00:00Z`).toISOString()
              : meta.sourceDateTime || new Date().toISOString(),
            submission_data: { ...decoded.values, _source: source },
          });

          if (pendingSubmissions.length >= SUBMISSION_BATCH) await flush();
        }
      };

      const { stopped } = await streamOdmSubjects(file, {
        onSubject: handleSubject,
        onWarning: (m) => {
          if (rep.errors.length < 200) rep.errors.push(m);
        },
        shouldStop: () => stopRef.current,
        onProgress: (p) => {
          setProgress({
            pct: p.totalBytes ? Math.round((p.bytesRead / p.totalBytes) * 100) : 0,
            subjects: p.subjectsSeen,
          });
          setReport({ ...rep });
        },
      });

      if (mode === "import") await flush();
      rep.stopped = stopped;
      rep.finishedAt = new Date().toISOString();
      setReport({ ...rep });
      setLastDryRunOk(mode === "dry_run" && !stopped);

      await logAuditTrail({
        action: mode === "dry_run" ? "redcap_odm_clinical_dry_run" : "redcap_odm_clinical_import",
        entityType: "patient_form_submissions",
        entityId: siteId,
        details: {
          mode,
          trial_site_id: siteId,
          source_file: file.name,
          study: meta.studyName || meta.studyOid,
          export_timestamp: meta.sourceDateTime,
          email_suffix: emailSuffix,
          subjects_seen: rep.subjectsSeen,
          subjects_matched: rep.subjectsMatched,
          subjects_unmatched: rep.subjectsSeen - rep.subjectsMatched,
          unmatched_subject_keys: rep.subjectsUnmatched.slice(0, 200),
          form_instances_seen: rep.formInstancesSeen,
          unknown_form_oids: rep.formInstancesUnknownForm,
          submissions_written: rep.submissionsWritten,
          submissions_skipped_existing: rep.submissionsSkippedExisting,
          submissions_skipped_empty: rep.submissionsSkippedEmpty,
          visits_created: rep.visitsCreated,
          visits_reused: rep.visitsReused,
          status_breakdown: rep.statusCounts,
          errors: rep.errors.slice(0, 50),
          stopped: rep.stopped,
          compliance: "21_cfr_part_11",
        },
      });

      if (mode === "dry_run") {
        toast.success(
          `Dry run complete — ${rep.subjectsMatched}/${rep.subjectsSeen} subjects matched`,
        );
      } else {
        toast.success(`Imported ${rep.submissionsWritten} submission(s)`);
      }
    } catch (err: any) {
      rep.errors.push(err?.message ?? String(err));
      rep.finishedAt = new Date().toISOString();
      setReport({ ...rep });
      toast.error("Migration run failed", { description: err?.message ?? String(err) });
    } finally {
      setRunning(false);
    }
  }

  const unmatchedCount = report ? report.subjectsSeen - report.subjectsMatched : 0;

  return (
    <>
      <SEOHead
        title="REDCap Clinical Data Migration — BRIDGE Admin"
        description="Stream historical REDCap ODM clinical data into BRIDGE patient visits and eCRF submissions."
      />
      <Layout>
        <main className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin Dashboard
            </Link>
          </Button>

          <header>
            <h1 className="text-3xl font-bold tracking-tight">
              REDCap historical clinical data (Phase 2)
            </h1>
            <p className="text-muted-foreground">
              Step 2 of the migration: bring across the recorded form data. Patient profiles are
              never created here — unmatched subjects are reported instead.
            </p>
          </header>

          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>This file contains patient identifiers</AlertTitle>
            <AlertDescription className="text-xs">
              The clinical section holds names, dates of birth, phone numbers and addresses. Work on
              a trusted device, and always run the dry run before importing.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Destination and subject key</CardTitle>
              <CardDescription>
                Each ODM SubjectKey is resolved to a patient through its placeholder email.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="site">Trial site</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger id="site" className="max-w-xl">
                    <SelectValue placeholder="Select a trial site" />
                  </SelectTrigger>
                  <SelectContent>
                    {(sites || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                        {s.country ? ` — ${s.country}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="suffix">Placeholder email suffix</Label>
                <Input
                  id="suffix"
                  className="max-w-xl font-mono text-sm"
                  value={emailSuffix}
                  onChange={(e) => setEmailSuffix(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Subject <span className="font-mono">1042</span> resolves to{" "}
                  <span className="font-mono">1042{emailSuffix}</span>.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Upload the same ODM export</CardTitle>
              <CardDescription>
                Only the metadata header is read up front. The clinical section is streamed in
                chunks, never held in memory as one document.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                type="file"
                accept=".xml,text/xml,application/xml"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              {parsingMeta && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Reading metadata header…
                </p>
              )}
              {metaError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Could not read the file</AlertTitle>
                  <AlertDescription className="text-xs">{metaError}</AlertDescription>
                </Alert>
              )}
              {meta && file && (
                <p className="text-sm text-muted-foreground">
                  {meta.studyName || meta.studyOid} · {meta.forms.length} form definition(s) ·{" "}
                  {(file.size / (1024 * 1024)).toFixed(1)} MB ·{" "}
                  {meta.sourceDateTime?.slice(0, 10) || "no export date"}
                </p>
              )}
            </CardContent>
          </Card>

          {meta && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">3. Form coverage at this site</CardTitle>
                <CardDescription>
                  Every exported form needs a matching eCRF definition from the Phase 1 import.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {missingForms.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{missingForms.length} form(s) not yet defined here</AlertTitle>
                    <AlertDescription className="text-xs">
                      Run the{" "}
                      <Link className="underline" to="/admin/redcap-odm-import">
                        Phase 1 form import
                      </Link>{" "}
                      for this site first — data for those forms cannot be written.
                    </AlertDescription>
                  </Alert>
                )}
                <ScrollArea className="h-[260px] rounded border">
                  <ul className="divide-y">
                    {formCoverage.map((f) => (
                      <li key={f.formOid} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{f.formName}</p>
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {f.formOid}
                          </p>
                        </div>
                        {f.targetFormId ? (
                          <Badge variant="secondary" className="shrink-0">
                            <CheckCircle2 className="mr-1 h-3 w-3" /> Mapped
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="shrink-0">
                            Missing
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {meta && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">4. Dry run, then import</CardTitle>
                <CardDescription>
                  The dry run writes nothing. It reports how many subjects resolve, how many form
                  instances would be created, and which subjects have no profile.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => run("dry_run")} disabled={!!running}>
                    {running === "dry_run" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Database className="mr-2 h-4 w-4" />
                    )}
                    Dry run
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => run("import")}
                    disabled={!!running || !lastDryRunOk || missingForms.length > 0}
                  >
                    {running === "import" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Database className="mr-2 h-4 w-4" />
                    )}
                    Import clinical data
                  </Button>
                  {running && (
                    <Button variant="outline" onClick={() => (stopRef.current = true)}>
                      <StopCircle className="mr-2 h-4 w-4" />
                      Stop
                    </Button>
                  )}
                </div>
                {!lastDryRunOk && (
                  <p className="text-xs text-muted-foreground">
                    Import unlocks after a dry run finishes without being stopped.
                  </p>
                )}
                {running && (
                  <div className="space-y-1">
                    <Progress value={progress.pct} />
                    <p className="text-xs text-muted-foreground">
                      {progress.pct}% of file read · {progress.subjects} subject(s) streamed
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {report && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {report.mode === "dry_run" ? "Dry run report" : "Import report"}
                </CardTitle>
                <CardDescription>
                  {report.finishedAt
                    ? `Finished ${new Date(report.finishedAt).toLocaleString()}`
                    : "Running…"}
                  {report.stopped ? " · stopped early" : ""} · logged to the audit trail
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Subjects streamed" value={report.subjectsSeen} />
                  <Stat label="Matched to a profile" value={report.subjectsMatched} />
                  <Stat label="Unmatched" value={unmatchedCount} tone={unmatchedCount ? "warn" : undefined} />
                  <Stat label="Form instances" value={report.formInstancesSeen} />
                  <Stat
                    label={report.mode === "dry_run" ? "Submissions planned" : "Submissions written"}
                    value={report.submissionsWritten}
                  />
                  <Stat label="Skipped (already imported)" value={report.submissionsSkippedExisting} />
                  <Stat label="Skipped (no data)" value={report.submissionsSkippedEmpty} />
                  <Stat
                    label={report.mode === "dry_run" ? "Visits needed" : "Visits created"}
                    value={report.visitsCreated}
                  />
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {Object.entries(report.statusCounts).map(([k, v]) => (
                    <Badge key={k} variant="outline">
                      {k}: {v}
                    </Badge>
                  ))}
                </div>

                {report.subjectsUnmatched.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Unmatched subject keys ({report.subjectsUnmatched.length}
                        {report.subjectsUnmatched.length >= 500 ? "+, first 500 shown" : ""})
                      </p>
                      <p className="text-xs text-muted-foreground">
                        No profile exists for these subjects. Nothing was created for them — import
                        the missing patients first if their data is needed.
                      </p>
                      <ScrollArea className="h-[140px] rounded border p-2">
                        <p className="font-mono text-xs leading-5 break-words">
                          {report.subjectsUnmatched.join(", ")}
                        </p>
                      </ScrollArea>
                    </div>
                  </>
                )}

                {Object.keys(report.formInstancesUnknownForm).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Form instances with no destination</p>
                      <ScrollArea className="h-[120px] rounded border p-2">
                        <ul className="space-y-1 text-xs">
                          {Object.entries(report.formInstancesUnknownForm).map(([oid, n]) => (
                            <li key={oid} className="font-mono">
                              {oid} — {n}
                            </li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  </>
                )}

                {report.errors.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-destructive">
                        Errors ({report.errors.length})
                      </p>
                      <ScrollArea className="h-[140px] rounded border p-2">
                        <ul className="space-y-1 text-xs">
                          {report.errors.map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </main>
      </Layout>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn";
}) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`text-xl font-semibold ${tone === "warn" ? "text-destructive" : ""}`}
      >
        {value.toLocaleString()}
      </p>
    </div>
  );
}
